import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
} from '@finn/design-system/atoms/modal';
import { Skeleton } from '@finn/design-system/atoms/skeleton';
import { AddTime } from '@finn/design-system/icons/add-time';
import { CarFilled } from '@finn/design-system/icons/car-filled';
import { SwapHoriz } from '@finn/design-system/icons/swap-horiz';
import { Deal } from '@finn/platform-modules';
import { SelfServiceRoutes } from '@finn/ua-constants';
import { ModalKey, useCloseModal, useOpenModal } from '@finn/ua-modals';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { getCloudinaryImgUrl, useCurrentLocale, useIntl } from '@finn/ui-utils';
import { formatDate } from '@finn/ui-utils/ssr';
import { useCallback } from 'react';

import { ActionButton } from './components/ActionButton';
import { useProlongationData } from './hooks/useProlongationData';

type Props = {
  open: boolean;
  onClose: () => void;
  onCloseCallback?: () => void;
  selectedSubscription?: Deal | null;
  autoOpened?: boolean;
  location?: string;
};

export const RetentionModal = ({
  open,
  onClose,
  onCloseCallback,
  selectedSubscription,
  autoOpened,
}: Props) => {
  const i18n = useIntl();
  const { locale } = useCurrentLocale();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const prolongationData = useProlongationData(selectedSubscription?.id);
  const openSwappablePLPPage = useCallback((subscription?: Deal | null) => {
    interactionTrackingEvent(TrackingEventName.SWAP_OPTION_CLICKED, {
      location: 'Retention Campaign Card PLP',
      SubscriptionId: subscription?.id,
    });
    const returnDate =
      selectedSubscription?.preferred_return_date ||
      selectedSubscription?.end_date;
    const fromDateSwap =
      selectedSubscription?.return_ranges?.internal_swap?.from;
    const toDateSwap = selectedSubscription?.return_ranges?.internal_swap?.to;
    if (fromDateSwap && toDateSwap) {
      window.location.href = `/${locale}/subscribe?available_from=${fromDateSwap}&available_to=${toDateSwap}`;
    } else {
      window.location.href = `/${locale}/subscribe?available_from=${returnDate?.split?.('T')?.[0]}`;
    }
  }, []);

  const openProlongationModal = useCallback(() => {
    interactionTrackingEvent(TrackingEventName.PROLONGATION_OPTION_CLICKED, {
      location: 'Retention Campaign Card PLP',
      SubscriptionId: selectedSubscription?.id,
    });
    closeModal(ModalKey.RETENTION_MODAL);
    openModal(ModalKey.PROLONGATION_MODAL);
  }, [closeModal, openModal, selectedSubscription?.id]);

  const title = i18n.formatMessage(
    autoOpened ? 'plp.retentionModal.titleLong' : 'plp.retentionModal.title'
  );
  const description = i18n.formatMessage(
    autoOpened
      ? 'plp.retentionModal.descriptionLong'
      : 'plp.retentionModal.description',
    { endDate: formatDate(selectedSubscription?.end_date, 'DD.MM.YYYY') }
  );
  const prolongTitle = i18n.formatMessage('plp.retentionModal.prolong.title');
  const prolongDescription = i18n.formatMessage(
    'plp.retentionModal.prolong.description'
  );
  const swapTitle = i18n.formatMessage('plp.retentionModal.swap.title');
  const swapDescription = i18n.formatMessage(
    'plp.retentionModal.swap.description'
  );
  const shouldShowReturnText =
    autoOpened &&
    selectedSubscription?.is_return_window_open &&
    !selectedSubscription?.return_type;

  return (
    <Modal
      variant="small"
      open={open}
      onOpenChange={(isOpen) => {
        !isOpen && onClose();
        !isOpen && onCloseCallback?.();
      }}
    >
      <ModalContent
        onPointerDownOutside={(e) => {
          if (autoOpened) {
            e.preventDefault();
          }
        }}
      >
        <ModalHeader>
          <ModalTitle>{title}</ModalTitle>
          {autoOpened && (
            <div className="flex items-center gap-4">
              {selectedSubscription?.car?.picture ? (
                <img
                  className="my-2 w-0 max-w-full basis-2/5 bg-snow mix-blend-multiply transition-transform sm:my-4"
                  src={getCloudinaryImgUrl(selectedSubscription.car.picture)}
                  alt={`${selectedSubscription.car.oem} ${selectedSubscription.car.model}`}
                />
              ) : (
                <CarFilled className="mx-8 my-2 h-full w-0 max-w-[192px] basis-1/2 opacity-50 sm:mx-12 sm:my-4" />
              )}
              <div className="flex w-0 basis-3/5 flex-col gap-2">
                <p className="body-16-semibold">{`${selectedSubscription?.car?.oem} ${selectedSubscription?.car?.model}`}</p>
                <p className="body-16-light">
                  {selectedSubscription?.car?.license_plate}
                </p>
              </div>
            </div>
          )}
          <ModalSubtitle>{description}</ModalSubtitle>
        </ModalHeader>
        <div className="mb-12 flex flex-col gap-4 md:mb-0">
          {prolongationData.isLoading ? (
            <Skeleton className="!mb-4 h-[60px] w-full !rounded-sm bg-pearl" />
          ) : (
            !!prolongationData.data?.prolongation_options?.length && (
              <ActionButton
                Icon={AddTime}
                title={prolongTitle}
                description={prolongDescription}
                onClick={openProlongationModal}
              />
            )
          )}
          <ActionButton
            Icon={SwapHoriz}
            title={swapTitle}
            description={swapDescription}
            onClick={() => {
              openSwappablePLPPage(selectedSubscription);
            }}
          />
          {shouldShowReturnText && (
            <p className="mt-4 body-14-light">
              Falls du dein{' '}
              <a
                className="inline-block underline body-14-semibold"
                href={`${SelfServiceRoutes.MY_CARS}/${selectedSubscription?.id}?modalName=MULTISTEP_EDIT_RETURN`}
              >
                Auto zurückgeben
              </a>{' '}
              möchtest, hoffen wir, dir bald wieder ein passendes Auto Abo
              anbieten zu können.
            </p>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};
